// ============================================================================
// SeriesManageModal — add / edit / archive series, plus Materialize.
// ============================================================================
// Series can hold a recurrence_pattern (freq/interval/byday/count/until)
// which is stored as jsonb on cal_core_series. The form builder below
// generates that JSON.
//
// Materialize (Round 3): when a series has a recurrence_pattern and at
// least one event in the series is marked is_recurring_template=true,
// the inline Materialize form on each series row generates N instance
// events from that template by calling POST /api/admin/series/:id/expand.
// Each instance is a SNAPSHOT — later template edits don't propagate.
// ============================================================================

import { useState } from 'react';
import { Pencil, Trash2, Plus, Loader2, Check, X, Repeat, Sparkles, Calendar } from 'lucide-react';
import { Modal } from './Modal';
import { api, type Series, type SeriesInput, type SeriesType, type RecurrenceFreq, type Weekday, type PublicEvent } from '../api';

interface Props {
  open: boolean;
  onClose: () => void;
  token: string;
  series: Series[];
  onRefresh: () => void;
  /** Render as a flat in-flow panel (no overlay). Used by the admin sidebar. */
  inline?: boolean;
}

interface FormState {
  seriesId: number | null;
  label: string;
  type: SeriesType;
  freq: RecurrenceFreq | '';
  interval: number;
  byday: Set<Weekday>;
  endMode: 'never' | 'count' | 'until';
  count: number;
  until: string;
  // Task #36: monthly recurrence sub-mode.
  //   'day-of-month'  — same DAY each month (current default; e.g. 8th of every month)
  //   'positional'    — Nth weekday of each month (e.g. 1st Monday)
  monthlyMode: 'day-of-month' | 'positional';
  // Position (1..4 or -1 for "last"). Active when monthlyMode='positional'.
  bysetpos: number;
  // Single weekday for positional monthly mode (separate from weekly byday set).
  positionalDay: Weekday;
}

const EMPTY_FORM: FormState = {
  seriesId: null,
  label: '',
  type: 'custom',
  freq: '',
  interval: 1,
  byday: new Set(),
  endMode: 'never',
  count: 4,
  until: '',
  monthlyMode: 'day-of-month',
  bysetpos: 1,
  positionalDay: 'MO',
};

const BYSETPOS_OPTIONS: { value: number; label: string }[] = [
  { value: 1,  label: 'first'  },
  { value: 2,  label: 'second' },
  { value: 3,  label: 'third'  },
  { value: 4,  label: 'fourth' },
  { value: -1, label: 'last'   },
];

const WEEKDAY_FULL: Record<Weekday, string> = {
  SU: 'Sunday', MO: 'Monday', TU: 'Tuesday', WE: 'Wednesday',
  TH: 'Thursday', FR: 'Friday', SA: 'Saturday',
};

const WEEKDAYS: { value: Weekday; label: string }[] = [
  { value: 'SU', label: 'Su' }, { value: 'MO', label: 'Mo' }, { value: 'TU', label: 'Tu' },
  { value: 'WE', label: 'We' }, { value: 'TH', label: 'Th' }, { value: 'FR', label: 'Fr' }, { value: 'SA', label: 'Sa' },
];

// Round 3: state for the inline Materialize form (one series at a time).
interface MatForm {
  seriesId: number;
  template_entry_id: number | null;
  start_date: string;       // YYYY-MM-DD; backend converts via the template's time-of-day
  max_instances: number;
}

export function SeriesManageModal({ open, onClose, token, series, onRefresh, inline = false }: Props) {
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Round 3: Materialize sub-flow.
  const [matForm, setMatForm] = useState<MatForm | null>(null);
  const [templates, setTemplates] = useState<PublicEvent[]>([]);
  const [matLoading, setMatLoading] = useState(false);
  const [matResult, setMatResult] = useState<{ count: number } | null>(null);

  const startNew = () => { setError(''); setForm(EMPTY_FORM); };

  const startEdit = (s: Series) => {
    setError('');
    const rp = s.recurrence_pattern;
    // Detect monthly positional mode: bysetpos set AND single byday weekday.
    const isPositional =
      rp?.freq === 'monthly' &&
      typeof rp?.bysetpos === 'number' &&
      Array.isArray(rp?.byday) &&
      rp.byday.length === 1;
    setForm({
      seriesId: s.series_id,
      label: s.series_label,
      type: s.series_type,
      freq: rp?.freq ?? '',
      interval: rp?.interval ?? 1,
      // For weekly, load the existing byday set. For monthly positional, we
      // keep byday EMPTY here (positionalDay holds the single weekday) so
      // the weekly UI doesn't accidentally light up multiple days.
      byday: rp?.freq === 'weekly' ? new Set(rp?.byday ?? []) : new Set(),
      endMode: rp?.until ? 'until' : rp?.count ? 'count' : 'never',
      count: rp?.count ?? 4,
      until: rp?.until ?? '',
      monthlyMode: isPositional ? 'positional' : 'day-of-month',
      bysetpos: isPositional ? (rp!.bysetpos as number) : 1,
      positionalDay: isPositional && rp?.byday ? (rp.byday[0] as Weekday) : 'MO',
    });
  };

  const cancelForm = () => { setForm(null); setError(''); };

  const save = async () => {
    if (!form) return;
    if (!form.label.trim()) { setError('Series label is required.'); return; }
    setError(''); setBusy(true);
    // Build the byday + bysetpos pair based on freq and (for monthly) mode.
    let byday: Weekday[] | undefined = undefined;
    let bysetpos: number | null = null;
    if (form.freq === 'weekly' && form.byday.size > 0) {
      byday = Array.from(form.byday);
    } else if (form.freq === 'monthly' && form.monthlyMode === 'positional') {
      byday = [form.positionalDay];
      bysetpos = form.bysetpos;
    }
    const payload: SeriesInput = {
      series_label: form.label.trim(),
      series_type: form.type,
      recurrence_pattern: form.freq
        ? {
            freq: form.freq,
            interval: form.interval,
            byday,
            bysetpos,
            count: form.endMode === 'count' ? form.count : null,
            until: form.endMode === 'until' && form.until ? form.until : null,
          }
        : null,
    };
    try {
      if (form.seriesId == null) await api.createSeries(token, payload);
      else                       await api.updateSeries(token, form.seriesId, payload);
      setForm(null);
      onRefresh();
    } catch (err: any) {
      setError(err?.message || 'Could not save series.');
    } finally {
      setBusy(false);
    }
  };

  const archive = async (s: Series) => {
    const yes = window.confirm(`Archive "${s.series_label}"? Events stay; only the series link is hidden.`);
    if (!yes) return;
    setBusy(true);
    try { await api.archiveSeries(token, s.series_id); onRefresh(); }
    catch (err: any) { alert(err?.message || 'Could not archive.'); }
    finally { setBusy(false); }
  };

  const toggleDay = (d: Weekday) => {
    if (!form) return;
    const next = new Set(form.byday);
    if (next.has(d)) next.delete(d); else next.add(d);
    setForm({ ...form, byday: next });
  };

  // -------------------------------------------------------------------------
  // Round 3: Materialize — open inline form, load templates for this series,
  // call /api/admin/series/:id/expand on submit.
  // -------------------------------------------------------------------------
  const canMaterialize = (s: Series) =>
    s.series_type !== 'multi_day' && !!s.recurrence_pattern?.freq;

  const startMaterialize = async (s: Series) => {
    setError('');
    setMatResult(null);
    setMatForm({
      seriesId: s.series_id,
      template_entry_id: null,
      start_date: '',
      max_instances: s.recurrence_pattern?.count ?? 4,
    });
    setTemplates([]);
    setMatLoading(true);
    try {
      // includePast lets us surface templates whose start_date is in the past
      // (templates are content-only — their date is meaningless for filtering).
      // includeTemplates opts into the UIF-only flag that lifts the default
      // is_recurring_template=false filter on /api/events — without it, the
      // public-facing handler hides templates from every caller.
      // includeDrafts: templates are often authored in draft mode (so the
      // admin iterates on content before materializing). Without this flag,
      // draft templates get filtered out by the default is_draft=false
      // clause and Materialize can never find them.
      const { events } = await api.events({ includePast: true, includeTemplates: true, includeDrafts: true, token });
      const ts = events.filter(
        (e) => e.series_id === s.series_id && e.is_recurring_template === true,
      );
      setTemplates(ts);
      if (ts.length === 0) {
        setError(
          'No recurring template found for this series. Open an event in Edit mode, ' +
          'link it to this series, and turn on "Use as recurring template" before materializing. ' +
          '(Draft + live templates both qualify.)',
        );
      }
    } catch (err: any) {
      setError(err?.message || 'Could not load templates.');
    } finally {
      setMatLoading(false);
    }
  };

  const cancelMaterialize = () => {
    setMatForm(null);
    setTemplates([]);
    setMatResult(null);
    setError('');
  };

  const doMaterialize = async () => {
    if (!matForm) return;
    if (!matForm.template_entry_id) { setError('Pick a template event.'); return; }
    if (!matForm.start_date)        { setError('Pick a start date for the first instance.'); return; }
    setError(''); setBusy(true);
    try {
      // We send start_date as an ISO timestamp at noon UTC; the backend uses
      // the template's start_date for time-of-day so this hour is irrelevant
      // (it only matters that it's a valid ISO datetime per the Zod schema).
      const isoStart = new Date(matForm.start_date + 'T12:00:00Z').toISOString();
      const result = await api.expandSeries(token, matForm.seriesId, {
        template_entry_id: matForm.template_entry_id,
        start_date: isoStart,
        max_instances: matForm.max_instances,
      });
      setMatResult({ count: result.count });
      onRefresh(); // parent re-fetches events list so new instances show up immediately
    } catch (err: any) {
      setError(err?.message || 'Could not materialize series.');
    } finally {
      setBusy(false);
    }
  };

  const resetMaterializeForMore = () => {
    if (!matForm) return;
    setMatResult(null);
    setMatForm({ ...matForm, start_date: '' });
  };

  return (
    <Modal open={open} onClose={onClose} title="Manage series library" maxWidth="max-w-2xl" inline={inline}>
      <div className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>}

        {form == null && (
          <div className="flex justify-end">
            <button type="button" onClick={startNew}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white px-3 py-1.5 text-sm font-semibold hover:bg-uif-green-dark">
              <Plus className="h-4 w-4" />
              Add new series
            </button>
          </div>
        )}

        {form != null && (
          <fieldset className="border border-uif-green/30 rounded-md p-4 space-y-3 bg-uif-green/5">
            <legend className="px-1 text-sm font-semibold text-uif-green-dark">
              {form.seriesId == null ? 'New series' : 'Edit series'}
            </legend>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Series label *</span>
              <input type="text" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Spring 2026 Food Safety Cohort"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
            </label>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Series type</span>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as SeriesType })}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm">
                <option value="custom">Custom — handpicked events</option>
                <option value="multi_day">Multi-day — single workshop spanning N consecutive days</option>
                <option value="weekly">Weekly — recurring sessions</option>
                <option value="monthly">Monthly — recurring sessions</option>
              </select>
            </label>

            <div className="bg-white rounded-md border border-gray-200 p-3 space-y-3">
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <Repeat className="h-3 w-3" />
                Recurrence pattern (optional)
              </div>

              <label className="block">
                <span className="block text-xs text-gray-600 mb-1">Frequency</span>
                <select value={form.freq} onChange={(e) => setForm({ ...form, freq: e.target.value as RecurrenceFreq | '' })}
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm">
                  <option value="">No recurrence</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </label>

              {form.freq && (
                <>
                  <label className="block">
                    <span className="block text-xs text-gray-600 mb-1">Every</span>
                    <div className="flex items-center gap-2">
                      <input type="number" min={1} max={99} value={form.interval}
                        onChange={(e) => setForm({ ...form, interval: Number(e.target.value) || 1 })}
                        className="w-20 rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
                      <span className="text-xs text-gray-600">
                        {form.freq === 'daily' ? 'days' : form.freq === 'weekly' ? 'weeks' : 'months'}
                      </span>
                    </div>
                  </label>

                  {form.freq === 'weekly' && (
                    <div>
                      <span className="block text-xs text-gray-600 mb-1">On days</span>
                      <div className="flex gap-1">
                        {WEEKDAYS.map((d) => (
                          <button key={d.value} type="button" onClick={() => toggleDay(d.value)}
                            className={'rounded-full w-8 h-8 text-xs font-semibold border transition-colors ' +
                              (form.byday.has(d.value)
                                ? 'bg-uif-green text-white border-uif-green-dark'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')}>
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {form.freq === 'monthly' && (
                    <div>
                      <span className="block text-xs text-gray-600 mb-1">Monthly pattern</span>
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            checked={form.monthlyMode === 'day-of-month'}
                            onChange={() => setForm({ ...form, monthlyMode: 'day-of-month' })}
                          />
                          On the same day each month
                          <span className="text-xs text-gray-500 italic">
                            (e.g. the 8th of every month)
                          </span>
                        </label>
                        <label className="flex items-center gap-2 text-sm flex-wrap">
                          <input
                            type="radio"
                            checked={form.monthlyMode === 'positional'}
                            onChange={() => setForm({ ...form, monthlyMode: 'positional' })}
                          />
                          On the
                          <select
                            value={form.bysetpos}
                            onChange={(e) => setForm({ ...form, bysetpos: Number(e.target.value), monthlyMode: 'positional' })}
                            disabled={form.monthlyMode !== 'positional'}
                            className="rounded-md border border-gray-300 px-2 py-0.5 text-sm disabled:bg-gray-50"
                          >
                            {BYSETPOS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          <select
                            value={form.positionalDay}
                            onChange={(e) => setForm({ ...form, positionalDay: e.target.value as Weekday, monthlyMode: 'positional' })}
                            disabled={form.monthlyMode !== 'positional'}
                            className="rounded-md border border-gray-300 px-2 py-0.5 text-sm disabled:bg-gray-50"
                          >
                            {WEEKDAYS.map((d) => (
                              <option key={d.value} value={d.value}>{WEEKDAY_FULL[d.value]}</option>
                            ))}
                          </select>
                          of the month
                        </label>
                      </div>
                    </div>
                  )}

                  <div>
                    <span className="block text-xs text-gray-600 mb-1">Ends</span>
                    <div className="space-y-1">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="radio" checked={form.endMode === 'never'} onChange={() => setForm({ ...form, endMode: 'never' })} />
                        Never
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="radio" checked={form.endMode === 'count'} onChange={() => setForm({ ...form, endMode: 'count' })} />
                        After
                        <input type="number" min={1} max={366} value={form.count}
                          onChange={(e) => setForm({ ...form, count: Number(e.target.value) || 1 })}
                          disabled={form.endMode !== 'count'}
                          className="w-16 rounded-md border border-gray-300 px-2 py-0.5 text-sm disabled:bg-gray-50" />
                        occurrences
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="radio" checked={form.endMode === 'until'} onChange={() => setForm({ ...form, endMode: 'until' })} />
                        On
                        <input type="date" value={form.until}
                          onChange={(e) => setForm({ ...form, until: e.target.value })}
                          disabled={form.endMode !== 'until'}
                          className="rounded-md border border-gray-300 px-2 py-0.5 text-sm disabled:bg-gray-50" />
                      </label>
                    </div>
                  </div>
                </>
              )}

              <p className="text-[11px] text-gray-500">
                Once a recurrence is saved, link a template event to this series and click <strong>Materialize</strong>
                on the series row to generate N occurrences in one shot. Each instance is a snapshot of the
                template at that moment — later template edits don't propagate to existing instances.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={cancelForm}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
              <button type="button" onClick={save} disabled={busy}
                className="inline-flex items-center gap-1 rounded-md bg-uif-green text-white px-3 py-1.5 text-sm font-semibold hover:bg-uif-green-dark disabled:bg-gray-300">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                {form.seriesId == null ? 'Add series' : 'Save changes'}
              </button>
            </div>
          </fieldset>
        )}

        {form == null && (
          <div className="space-y-2">
            {series.length === 0 && (
              <p className="text-sm text-gray-500 italic text-center py-4">No series yet. Click "Add new series" above.</p>
            )}
            {series.map((s) => (
              <div key={s.series_id}>
                <div className="flex items-center gap-3 border border-gray-200 rounded-md p-3 hover:border-uif-green/40">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-900 truncate">{s.series_label}</div>
                    <div className="text-xs text-gray-600">
                      {s.series_type}
                      {s.recurrence_pattern && ` · every ${s.recurrence_pattern.interval} ${s.recurrence_pattern.freq}`}
                    </div>
                  </div>
                  {canMaterialize(s) && matForm?.seriesId !== s.series_id && (
                    <button type="button" onClick={() => startMaterialize(s)} disabled={busy || matForm != null}
                      className="inline-flex items-center gap-1 rounded border border-uif-green/40 bg-uif-green/5 px-2 py-1 text-xs font-semibold text-uif-green-dark hover:bg-uif-green/10 disabled:opacity-50"
                      title="Generate N occurrences from a recurring template">
                      <Sparkles className="h-3 w-3" /> Materialize
                    </button>
                  )}
                  <button type="button" onClick={() => startEdit(s)} disabled={busy || matForm != null}
                    className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                  <button type="button" onClick={() => archive(s)} disabled={busy || matForm != null}
                    className="inline-flex items-center gap-1 rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50">
                    <Trash2 className="h-3 w-3" /> Archive
                  </button>
                </div>

                {matForm?.seriesId === s.series_id && (
                  <div className="mt-2 ml-4 border border-uif-green/30 rounded-md p-4 bg-uif-green/5 space-y-3">
                    <div className="text-xs font-semibold text-uif-green-dark uppercase tracking-wider flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3" />
                      Materialize "{s.series_label}"
                    </div>

                    {matLoading && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading templates linked to this series…
                      </div>
                    )}

                    {!matLoading && matResult && (
                      <div className="bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm text-green-800">
                        <strong>Success!</strong> Created {matResult.count} instance{matResult.count === 1 ? '' : 's'}.
                        They're in your events list now.
                        <div className="mt-2 flex gap-2">
                          <button type="button" onClick={cancelMaterialize}
                            className="inline-flex items-center gap-1 rounded-md bg-uif-green text-white px-3 py-1.5 text-xs font-semibold hover:bg-uif-green-dark">
                            <Check className="h-3 w-3" /> Done
                          </button>
                          <button type="button" onClick={resetMaterializeForMore}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
                            <Plus className="h-3 w-3" /> Materialize more
                          </button>
                        </div>
                      </div>
                    )}

                    {!matLoading && !matResult && (
                      <>
                        <label className="block">
                          <span className="block text-xs font-medium text-gray-700 mb-1">Template event *</span>
                          <div className="flex items-center gap-2">
                            <select
                              value={matForm.template_entry_id ?? ''}
                              onChange={(e) => setMatForm({ ...matForm, template_entry_id: e.target.value ? Number(e.target.value) : null })}
                              disabled={templates.length === 0}
                              className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:bg-gray-50">
                              <option value="">— pick a template —</option>
                              {templates.map((t) => (
                                <option key={t.calendar_entry_id} value={t.calendar_entry_id}>
                                  {t.title}
                                </option>
                              ))}
                            </select>
                            {/* Task #38: Edit template link. Opens the EventForm
                                for the selected template in a new tab (auth is
                                now shared via localStorage so this works). Lets
                                the admin tweak template content without losing
                                the Materialize form state. */}
                            {matForm.template_entry_id != null && (
                              <a
                                href={`#admin/events/edit/${matForm.template_entry_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                                title="Open this template in the event editor"
                              >
                                <Pencil className="h-3 w-3" /> Edit template
                              </a>
                            )}
                          </div>
                          {templates.length === 0 && !error && (
                            <p className="mt-1 text-[11px] text-gray-500">
                              No templates available. Edit an event, link it to this series, and turn on
                              "Use as recurring template" before materializing.
                            </p>
                          )}
                        </label>

                        <div className="grid grid-cols-2 gap-3">
                          <label className="block">
                            <span className="block text-xs font-medium text-gray-700 mb-1">First instance date *</span>
                            <input type="date"
                              value={matForm.start_date}
                              onChange={(e) => setMatForm({ ...matForm, start_date: e.target.value })}
                              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
                          </label>
                          <label className="block">
                            <span className="block text-xs font-medium text-gray-700 mb-1">Number of instances *</span>
                            <input type="number" min={1} max={52}
                              value={matForm.max_instances}
                              onChange={(e) => setMatForm({ ...matForm, max_instances: Math.max(1, Math.min(52, Number(e.target.value) || 1)) })}
                              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
                          </label>
                        </div>

                        <p className="text-[11px] text-gray-500 flex items-start gap-1.5">
                          <Calendar className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          Time-of-day comes from the template. Each instance is a snapshot — later template edits
                          won't propagate to already-materialized instances.
                        </p>

                        <div className="flex justify-end gap-2 pt-1">
                          <button type="button" onClick={cancelMaterialize}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                            <X className="h-3.5 w-3.5" /> Cancel
                          </button>
                          <button type="button" onClick={doMaterialize} disabled={busy || !matForm.template_entry_id || !matForm.start_date}
                            className="inline-flex items-center gap-1 rounded-md bg-uif-green text-white px-3 py-1.5 text-sm font-semibold hover:bg-uif-green-dark disabled:bg-gray-300">
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                            Materialize {matForm.max_instances} instance{matForm.max_instances === 1 ? '' : 's'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
